import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { useUser } from '../hooks/useUser'
import { useRecheckProfileLink } from '../hooks/useRecheckProfileLink'
import { TopCallout, navStore, CALLOUT_HEIGHT } from './nav/Nav'
import { Icon } from '@blueprintjs/core'
import { useDispatch, useSelector } from 'react-redux'
import { clearAndFetchUser } from '../actions/user'
import { axiosGet } from '../util/api'
import { useLocation, useNavigate } from 'react-router-dom'
import { createSelector } from 'reselect'
import { timeDifference } from '../util/helpers'
import { isNavV2Enabled } from '../util/nav-version'
import { Button } from './ui/button'

const hasCards = createSelector(
    (store: any) => store.cards.cards,
    (cards) => cards.length > 0,
)

interface TopCalloutsProps {
    setCalloutsCount: Dispatch<SetStateAction<number>>
}

export const TopCallouts = ({ setCalloutsCount }: TopCalloutsProps): JSX.Element => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { user, activeProfile, activeSubscription, invalidKeys } = useUser()
    const userHasCard = useSelector<boolean>(hasCards)
    const { search } = useLocation()
    // When nav v2 is active, the subscription/trial callouts are rendered
    // inside the nav (NavSubscriptionWarning) instead of as a top banner.
    const useNavV2 = isNavV2Enabled(search)
    // MER-2468: shared hook so this banner and the data-connections table
    // run literally the same code path on click.
    const { recheck, isRechecking } = useRecheckProfileLink()
    const recheckingLink = activeProfile ? isRechecking(activeProfile.profile_id) : false

    const recheckActiveProfileLink = () => {
        if (!activeProfile) {
            return
        }
        recheck(activeProfile)
    }

    const trialDaysLeftVerbiage = (): string => {
        const start = Date.now()
        const end = new Date((activeSubscription?.trial_end ?? 0) * 1000).getTime()

        const { daysDifference, hoursDifference } = timeDifference(end, start)
        const days: number = hoursDifference >= 12 ? daysDifference + 1 : daysDifference
        const daysVerbiage: string = days > 1 ? 'days' : 'day'

        if (daysDifference == 0) {
            if (hoursDifference <= 1) {
                return `Your trial is about to expire.`
            }

            if (hoursDifference <= 12) {
                return `Your trial is expiring today. Only ${hoursDifference} hours left`
            }
        }

        return `You have ${hoursDifference >= 12 ? daysDifference + 1 : daysDifference} ${daysVerbiage} left until the end of your trial period.`
    }

    // @todo memoize all callouts?
    const trialCallout = useMemo(() => {
        if (!!activeSubscription) {
            if (activeSubscription.isTrialing()) {
                return (
                    <TopCallout id="trial-countdown" className="mj-top-callout">
                        {trialDaysLeftVerbiage()}
                        <Button
                            className="ml-2"
                            text="Subscribe Now"
                            onClick={() => navigate('/billing')}
                        />
                    </TopCallout>
                )
            }
        }
    }, [JSON.stringify(activeSubscription)])

    const sortedCalloutsByPriority = [
        // reactivate trial. we are probably gonna need this again
        // {
        //     condition: user && !user?.is_admin && !activeSubscription,
        //     callout: <TopCallout key="reactivate">
        //         Reactivate your 30 day trial by the end of the year.&nbsp;
        //         <Button onClick={() => reactivateTrial(user.id)}>Reactivate</Button>
        //     </TopCallout>
        // },
        // Highest-priority callout. App.jsx renders TopCallouts on /onboard too
        // when impersonating, so an admin impersonating a keyless user (who gets
        // force-redirected to /onboard) always has a "Stop impersonating" escape
        // hatch. Because this is priority 0, renderCallouts shows only this one
        // while impersonating — no other callouts leak onto the onboard screen.
        {
            condition: user?.impersonating,
            callout: (
                <TopCallout className="mj-top-callout">
                    You are impersonating {user?.email}
                    (id #{user?.impersonating?.impersonate}
                    {activeProfile?.profile_id ? ' profile #' + activeProfile?.profile_id : ''})
                    from an admin account
                    <Button
                        className="ml-2"
                        text="Stop impersonating"
                        onClick={() => {
                            axiosGet('admin/stop-impersonating')
                                .then((res) => {
                                    console.log('res', res)
                                    dispatch(clearAndFetchUser())
                                    window.location.reload()
                                })
                                .catch((err) => {
                                    console.log('err', err)
                                })
                        }}
                    />
                </TopCallout>
            ),
        },
        {
            condition: invalidKeys,
            callout: (
                <TopCallout
                    key="no-linked-acc"
                    icon={<Icon icon="warning-sign" className="mj-top-callout text-red-500 mr-2" />}
                >
                    <b>Your Amazon Account is no longer linked with Merch Jar.</b>&nbsp; Your
                    automations and data syncs will not work until your account is relinked.
                    <Button
                        className="ml-2"
                        text="Link Account"
                        onClick={() => navigate('/keys')}
                    />
                    .
                </TopCallout>
            ),
        },
        {
            // MER-2468: per-profile API access lost. Distinct from `invalidKeys`
            // (which is all-keys-revoked); a single profile can be revoked while
            // others on the same keys remain healthy. Persistent (no dismiss) so
            // the user can't hide a state they need to act on. Only renders when
            // the active profile has unlinked_at set.
            //
            // Layout note: TopCallout defaults to a 40px-tall single flex row
            // with `justify-center`, which doesn't fit a title + explanation +
            // two action buttons. We bump the height and lay out the children
            // ourselves: a left column with hierarchy (title / body) and a
            // right cluster of buttons. The outer max-w cap keeps it readable
            // on ultra-wide monitors.
            condition: !!activeProfile?.unlinked_at,
            height: 84,
            callout: (
                <TopCallout
                    key="profile-unlinked"
                    height={84}
                    className="bg-red-100 border-b border-red-200"
                    icon={<Icon icon="offline" className="text-red-500 mr-3" size={20} />}
                >
                    <div className="flex items-center gap-6 w-full" style={{ maxWidth: 1100 }}>
                        <div className="flex-1 min-w-0 text-left">
                            <div className="font-semibold text-red-900 leading-tight">
                                API access to this profile lost
                                {activeProfile?.unlinked_at && (
                                    <span className="font-normal text-red-700 ml-1">
                                        {new Date(activeProfile.unlinked_at).toLocaleDateString()}
                                    </span>
                                )}
                            </div>
                            <div className="text-sm text-gray-700 leading-snug mt-0.5">
                                This usually means MerchJar's access was revoked in Amazon Ads. Data
                                syncs cannot be performed for this profile until it's reconnected.
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                                text="Re-check connection"
                                icon="refresh"
                                loading={recheckingLink}
                                onClick={recheckActiveProfileLink}
                            />
                        </div>
                    </div>
                </TopCallout>
            ),
        },
        {
            condition:
                !userHasCard &&
                (location.pathname === '/billing' || location.pathname === '/billing/cards'),
            callout: (
                <TopCallout className="mj-top-callout" key="add-cc">
                    To subscribe to any of the plans, please add your credit card
                    <Button
                        className="ml-2"
                        text="Add Card"
                        onClick={() => navigate('/billing/cards')}
                    />
                </TopCallout>
            ),
        },
        {
            // Suppressed when v2 nav is active — rendered as
            // NavSubscriptionWarning inside the sidebar instead.
            condition: !useNavV2 && user && !activeSubscription,
            callout: (
                <TopCallout
                    key="no-sub"
                    className="mj-top-callout bg-red-200 font-bold"
                    id="trial-expired"
                    icon={<Icon icon="warning-sign" className="text-red-500 mr-2" />}
                >
                    You don't have an active subscription. Please subscribe to continue using Merch
                    Jar.
                    <Button
                        className="ml-2"
                        text="Subscribe Now"
                        onClick={() => navigate('/billing')}
                    />
                </TopCallout>
            ),
        },
        {
            condition: activeProfile?.update_info?.success_count === 0,
            callout: (
                <TopCallout className="mj-top-callout" key="data-pull-in-progress">
                    <b>MerchJar is still pulling in your data!</b>&nbsp;This process usually takes
                    15-20 minutes, depending on the size of your account. We'll send you an email
                    when it's complete.
                </TopCallout>
            ),
        },
        {
            // Suppressed when v2 nav is active — rendered as
            // NavSubscriptionWarning inside the sidebar instead.
            condition: !useNavV2 && !!activeSubscription && activeSubscription.isTrialing(),
            callout: trialCallout,
        },
    ]

    useEffect(() => {
        let count = 0
        let bannerH = 0
        for (let i = 0; i < sortedCalloutsByPriority.length; i++) {
            const entry = sortedCalloutsByPriority[i]
            if (entry.condition) {
                count++
                // Only the first visible callout is rendered; use its height for layout.
                if (bannerH === 0) {
                    bannerH = (entry as any).height ?? CALLOUT_HEIGHT
                }
            }
        }

        setCalloutsCount(count)
        navStore.setBannerHeight(bannerH)
    }, [sortedCalloutsByPriority])

    const renderCallouts = () => {
        for (let i = 0; i < sortedCalloutsByPriority.length; i++) {
            if (sortedCalloutsByPriority[i].condition) {
                return sortedCalloutsByPriority[i].callout
            }
        }
    }

    // use this when we have hidable banners
    // const hideBanner = ({name}: {name: string }) => {
    //     axiosPatch('banners/hide', {
    //         bannerName: name,
    //     }).then((res) => {
    //         dispatch(refreshUser())
    //     })
    // }

    // reactivate trial. we are probably gonna need this again
    // const reactivateTrial = (userId) => {
    //     axiosPost('user-settings/reactivate-subscription', {
    //         days: 30,
    //         user_id: userId
    //     }).then((res) => {
    //         Toasts.success(`Added 30 trial days to your subscription!`)
    //         dispatch(clearAndFetchUser())
    //     }).catch((res) => {
    //         console.log(res)
    //     })
    // }

    return (
        <div>
            {/* purposefully commented for easy reuse on next sale */}
            {/* {user?.black_friday_2021_banner && <TopCallout id="black-friday-2021-banner">
            Black Friday - Up to 50% off!
            <Button className="ml-2" text="View" onClick={() => navigate('/billing/coin-bundles')}/>
            <Button className="ml-2" minimal icon="small-cross"
                onClick={() => hideBanner({name: 'black_friday_2021_banner'})}/>
        </TopCallout>} */}
            {renderCallouts()}
        </div>
    )
}
